import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';
import { useAuth } from './AuthContext';

const ActivityFeedContext = createContext();

export const useActivityFeed = () => {
  const context = useContext(ActivityFeedContext);
  if (!context) {
    throw new Error('useActivityFeed must be used within an ActivityFeedProvider');
  }
  return context;
};

export const ActivityFeedProvider = ({ children }) => {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, companies, users, jobs

  // Fetch unified feed
  const fetchFeed = async (type = filterType) => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const endpoint = type === 'all' ? '/feed' : `/feed/${type}`;
      const response = await ApiService.get(endpoint);
      if (response.success) {
        setFeed(response.data || []);
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to fetch feed';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Like activity
  const likeActivity = async (activityId, activityType) => {
    try {
      const endpoint = activityType === 'company_activity' 
        ? `/company-activities/${activityId}/like`
        : `/user-activities/${activityId}/like`;
      const response = await ApiService.post(endpoint);
      if (response.success) {
        // Update feed item
        setFeed(prevFeed => prevFeed.map(item => {
          if (item.activity_id === activityId && item.activity_type === activityType) {
            return {
              ...item,
              content: {
                ...item.content,
                likes_count: (item.content.likes_count || 0) + 1
              }
            };
          }
          return item;
        }));
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  // Unlike activity
  const unlikeActivity = async (activityId, activityType) => {
    try {
      const endpoint = activityType === 'company_activity' 
        ? `/company-activities/${activityId}/like`
        : `/user-activities/${activityId}/like`;
      const response = await ApiService.delete(endpoint);
      if (response.success) {
        // Update feed item
        setFeed(prevFeed => prevFeed.map(item => {
          if (item.activity_id === activityId && item.activity_type === activityType) {
            return {
              ...item,
              content: {
                ...item.content,
                likes_count: Math.max(0, (item.content.likes_count || 1) - 1)
              }
            };
          }
          return item;
        }));
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  // Create user activity
  const createUserActivity = async (activityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.post('/user-activities', activityData);
      if (response.success) {
        await fetchFeed(); // Refresh feed
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to create activity';
      setError(errorMsg);
      return { success: false, message: errorMsg, errors: err.data?.errors };
    } finally {
      setLoading(false);
    }
  };

  // Create company activity
  const createCompanyActivity = async (activityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.post('/company-activities', activityData);
      if (response.success) {
        await fetchFeed(); // Refresh feed
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to create activity';
      setError(errorMsg);
      return { success: false, message: errorMsg, errors: err.data?.errors };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchFeed();
    }
  }, [user, filterType]);

  const value = {
    feed,
    loading,
    error,
    filterType,
    setFilterType,
    fetchFeed,
    likeActivity,
    unlikeActivity,
    createUserActivity,
    createCompanyActivity,
  };

  return (
    <ActivityFeedContext.Provider value={value}>
      {children}
    </ActivityFeedContext.Provider>
  );
};

